import { Button, Modal } from '@dev-spendesk/grapes';
import { useEffect, useReducer, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import * as mfaSelectors from 'common/components/ModalMFA/selectors';
import { type Token } from 'common/components/TokenInput';
import { useTranslation } from 'common/hooks/useTranslation';
import type { AppDispatch } from 'modules/app/redux/store';

import { ModalMFATokenContent } from './ModalMFATokenContent';
import {
  regenerateProcedure,
  validateMFAToken,
  resetTokenVerificationStates,
} from '../actions';

const MFA_TOKEN_LENGTH = 6;
const emptyMfaToken = Array.from({ length: MFA_TOKEN_LENGTH }, () => '');

type FlowProps =
  | { flow: 'auth'; scaToken: string }
  | { flow: 'standard'; scaToken?: never };

type MfaTokenModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onMfaTokenValidated: () => void;
  otpChannel?: 'email' | 'phone';
  title?: string;
} & FlowProps;

type TokenState = {
  token: Token;
  isTokenFilled: boolean;
};

const tokenReducer = (_: TokenState, action: Token) => {
  // make sure the new token size is always equal to MFA_TOKEN_LENGTH
  const newToken = action.concat(emptyMfaToken).slice(0, MFA_TOKEN_LENGTH);
  return {
    token: newToken,
    isTokenFilled: !newToken.includes(''),
  };
};

export const ModalMFAToken = ({
  isOpen,
  onClose,
  flow,
  scaToken,
  onMfaTokenValidated,
  otpChannel,
  title,
}: MfaTokenModalProps) => {
  const { t } = useTranslation('global');
  const dispatch = useDispatch<AppDispatch>();
  const [isConfirmDeviceModalOpen, setIsConfirmDeviceModalOpen] =
    useState(false);
  const isValidatingMfaToken = useSelector(mfaSelectors.isValidatingMfaToken);
  const isMfaTokenValid = useSelector(mfaSelectors.getIsMfaTokenValid);
  const isMfaTokenVerified = useSelector(mfaSelectors.getIsMfaTokenVerified);

  const [{ token, isTokenFilled }, setToken] = useReducer(tokenReducer, {
    token: emptyMfaToken,
    isTokenFilled: false,
  });
  const [hasRegeneratedMfaProcedure, setHasRegeneratedMfaProcedure] =
    useState<boolean>(false);

  const resetState = () => {
    setToken(emptyMfaToken);
    setHasRegeneratedMfaProcedure(false);
    dispatch(resetTokenVerificationStates());
  };

  useEffect(() => {
    if (isMfaTokenVerified && isMfaTokenValid && isOpen) {
      onMfaTokenValidated();
    }
  }, [isMfaTokenVerified, isMfaTokenValid]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleValidateToken = (validateTrustedDevice: boolean) => {
    dispatch(
      validateMFAToken(token.join(''), flow, scaToken, validateTrustedDevice),
    );
  };

  const handleRegenerateProcedure = async () => {
    setToken(emptyMfaToken);
    await dispatch(regenerateProcedure(flow, scaToken));
    setHasRegeneratedMfaProcedure(true);
  };

  return (
    <>
      <Modal
        actions={[
          <Button
            key="cancel"
            onClick={onClose}
            text={t('misc.cancel')}
            variant="secondaryNeutral"
          />,
          <Button
            key="continue"
            onClick={() => {
              if (flow === 'auth') {
                setIsConfirmDeviceModalOpen(true);
              } else {
                handleValidateToken(false);
              }
            }}
            text={t('mfa.modal.code.confirmCodeButton')}
            variant="primaryBrand"
            form="mfa-form"
            type="submit"
            isDisabled={!isTokenFilled || isValidatingMfaToken}
          />,
        ]}
        iconName="lock-close"
        iconVariant="info"
        title={title || t('mfa.modal.code.title')}
        isOpen={isOpen}
      >
        <ModalMFATokenContent
          hasRegeneratedMfaProcedure={hasRegeneratedMfaProcedure}
          isMfaTokenVerified={isMfaTokenVerified}
          isMfaTokenValid={isMfaTokenValid}
          token={token}
          tokenLength={MFA_TOKEN_LENGTH}
          handleTokenChange={setToken}
          handleRegenerateProcedure={handleRegenerateProcedure}
          otpChannel={otpChannel}
        />
      </Modal>
      <Modal
        isOpen={isConfirmDeviceModalOpen}
        title={t('profile.trustedDevices.confirmationModal.title')}
        iconName="shield-star"
        iconVariant="info"
        actions={[
          <Button
            key="cancel"
            variant="secondaryNeutral"
            text={t('profile.trustedDevices.confirmationModal.notNow')}
            onClick={() => {
              handleValidateToken(false);
              setIsConfirmDeviceModalOpen(false);
            }}
          />,
          <Button
            key="confirm"
            variant="primaryBrand"
            text={t('profile.trustedDevices.confirmationModal.trust')}
            onClick={() => {
              handleValidateToken(true);
              setIsConfirmDeviceModalOpen(false);
            }}
          />,
        ]}
      >
        <p>{t('profile.trustedDevices.confirmationModal.description')}</p>
      </Modal>
    </>
  );
};
